import React, { useEffect, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import {
	Wrapper,
	ButtonWrapper,
	DescriptionText,
	DropWrapper,
	DropZoneArea,
	DropZoneText,
	IconWrapper,
	UploadInProgressWrapper,
	LoaderWrapper,
	ExampleDownload,
	DownloadLink,
	DeleteDataWrapper,
	Sidebar,
	Section,
	Title,
	Description,
	BottomSection,
	Content,
} from './UploadData.styles';
import {
	ApButtonDanger,
	ApButtonMain,
	ApIcon,
	ApLoaderDefault,
	ApLoaderText,
	colors,
} from '@alixpartners/ui-components';
import {
	createDAndIFile,
	deleteDandIData,
	getClientDataUploadStatus,
	getDandIClientDataTemplate,
	uploadFile,
	validateDAndIFile,
} from 'utils/apiRequests';
import FileList from '../FileList/FileList';
import { useClientDataUploadHistoryContext } from 'contexts/SupplierDiversity/ClientDataUploadHistoryProvider';
import { ApSignalrConsumer } from '@alixpartners/ui-utils';
import DeleteAllDataModal from '../components/DeleteAllDataModal/DeleteAllDataModal';
import { useToastMessageContext } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { NotificationTypes } from 'components/Toast/constants';
import { useAnalyzeEditDataContext } from 'contexts/SupplierDiversity/AnalyzeEditDataProvider';

const UploadData = () => {
	const [isUploading, setIsUploading] = useState(false);
	const [isUploadStatusFetching, setIsUploadStatusFetching] = useState(true);
	const [isUploadAllowed, setIsUploadAllowed] = useState(false);
	const [isLastUploadCompleted, setIsLastUploadCompleted] = useState(false);
	const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDeleteHistory, setIsDeleteHistory] = useState(true);
	const {
		isHistoryFetching,
		uploadedList,
		getHistoryData,
	} = useClientDataUploadHistoryContext();
	const { showToast } = useToastMessageContext();
	const { updateColDefs } = useAnalyzeEditDataContext();

	const dropzoneRef = useRef();

	useEffect(
		() => {
			getUploadStatus();
		}, // eslint-disable-next-line
		[]
	);

	const getUploadStatus = async () => {
		setIsUploadStatusFetching(true);
		const response = await getClientDataUploadStatus();
		if (response.succeeded) {
			if (response.data !== null && response.data.length > 0) {
				setIsUploadAllowed(false);
				if (
					response.data[0].pipeline_Status === 'success' ||
					response.data[0].pipeline_Status === 'failed'
				) {
					setIsLastUploadCompleted(true);
				}
			} else {
				setIsUploadAllowed(true);
				updateColDefs();
			}
			setIsUploadStatusFetching(false);
		} else {
			setIsUploadStatusFetching(false);
		}
	};

	const onFileSelected = acceptedFiles => {
		setIsUploadAllowed(false);
		setIsUploading(true);
		const file = acceptedFiles[0];
		createDAndIFile(file).then(response => {
			if (response.succeeded) {
				const { id, url } = response.data;
				uploadFile(url, file).then(() =>
					validateDAndIFile(id).then(validateResult => {
						if (validateResult.succeeded) {
							showToast(
								'File validated successfully, uploading is in progress.',
								NotificationTypes.success
							);
							getHistoryData();
							setIsUploading(false);
						} else {
							showToast(
								'File validattion failed. Please try again.',
								NotificationTypes.success
							);
							setIsUploading(false);
						}
					})
				);
			} else {
				showToast(
					'Error occured while uploading the file. Please try again.',
					NotificationTypes.danger
				);
				setIsUploading(false);
				setIsUploadAllowed(true);
			}
		});
	};

	const handleDeleteAllData = () => {
		setIsDeleting(true);
		deleteDandIData(isDeleteHistory)
			.then(response => {
				if (response.succeeded) {
					setIsDeleting(false);
					setDeleteModalOpen(false);
					setIsUploadAllowed(true);
					getHistoryData();
					showToast('Data deleted successfully.', NotificationTypes.success);
				} else {
					setDeleteModalOpen(false);
					showToast(
						'Error occured while deletig the file. Please try again.',
						NotificationTypes.danger
					);
				}
			})
			.catch(() => {
				setIsDeleting(false);
			});
	};

	const onCheckDeleteHistoryHandler = () => {
		setIsDeleteHistory(prevState => !prevState);
	};

	const renderInitialBlock = () => {
		return (
			<>
				<IconWrapper>
					<ApIcon
						iconName="cloud_upload"
						iconSize="96px"
						iconColor={colors.grey3}
					/>
				</IconWrapper>
				<DropZoneText>
					<DescriptionText>
						Drag <span>.csv</span> file here or browse files to upload
					</DescriptionText>
					<ButtonWrapper>
						<ApButtonMain iconName="baseline_add">Add file</ApButtonMain>
					</ButtonWrapper>
				</DropZoneText>
			</>
		);
	};

	return (
		<Wrapper>
			<Sidebar>
				<Section>
					<div>
						<Title>Data upload</Title>
						<Description>
							<p>Data upload process:</p>
							<p>1. Upload file into the system</p>
						</Description>
					</div>
					<div>
						<BottomSection>
							<Title>Delete data</Title>
							<Description>
								Data deletion clears all the Supplier Diversity data from the
								application.
							</Description>
							<DeleteDataWrapper>
								<ApButtonDanger
									title={
										!isLastUploadCompleted ? 'Data not present.' : 'Delete'
									}
									iconName="delete"
									onClick={e => {
										e.preventDefault();
										setDeleteModalOpen(true);
									}}
									disabled={!isLastUploadCompleted}
								>
									Delete all data
								</ApButtonDanger>
							</DeleteDataWrapper>
						</BottomSection>
					</div>
				</Section>
			</Sidebar>
			<Content>
				{isUploadStatusFetching ? (
					<LoaderWrapper>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoaderWrapper>
				) : (
					<>
						{isUploadAllowed ? (
							<Dropzone
								onDrop={onFileSelected}
								style={{}}
								noKeyboard
								ref={dropzoneRef}
							>
								{({ getRootProps, getInputProps }) => (
									<DropWrapper {...getRootProps()}>
										<input {...getInputProps()} />
										<DropZoneArea>
											<>{renderInitialBlock()}</>
										</DropZoneArea>
									</DropWrapper>
								)}
							</Dropzone>
						) : (
							<>
								{isUploading ? (
									<LoaderWrapper>
										<ApLoaderDefault loaderText={() => <ApLoaderText />} />
									</LoaderWrapper>
								) : (
									<>
										{isLastUploadCompleted ? (
											<UploadInProgressWrapper>
												Client data has been uploaded. To re-upload, delete all
												data first.
											</UploadInProgressWrapper>
										) : (
											<UploadInProgressWrapper>
												Uploading client data is in progress. Upload option will
												be enabled when the current execution is complete.
											</UploadInProgressWrapper>
										)}
									</>
								)}
							</>
						)}
					</>
				)}

				{isHistoryFetching ? (
					<LoaderWrapper>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoaderWrapper>
				) : (
					<FileList list={uploadedList} />
				)}

				<ExampleDownload>
					<span>
						Please be sure you are uploading an data in CSV format. You can
						download an example of the expected format below.
					</span>
					<DownloadLink
						href={getDandIClientDataTemplate()}
						target="_blank"
						download="template.csv"
					>
						Download example
					</DownloadLink>
				</ExampleDownload>

				{isDeleteModalOpen && (
					<DeleteAllDataModal
						onConfirm={handleDeleteAllData}
						onCancel={() => (!isDeleting ? setDeleteModalOpen(false) : null)}
						inProgress={isDeleting}
						isDeleteHistory={isDeleteHistory}
						onCheckDeleteHistoryHandler={onCheckDeleteHistoryHandler}
					/>
				)}

				<ApSignalrConsumer
					message="FetchDandIPipelineFinalStatus"
					onMessage={() => {
						getHistoryData();
					}}
				/>
				<ApSignalrConsumer
					message="FetchDandIPipelineSuccessStatus"
					onMessage={() => {
						getHistoryData();
						getUploadStatus();
					}}
				/>
				<ApSignalrConsumer
					message="FetchDandIPipelineFailureStatus"
					onMessage={() => {
						getUploadStatus();
						getHistoryData();
					}}
				/>
			</Content>
		</Wrapper>
	);
};

export default UploadData;
